import { Card, Skeleton } from '@mantine/core';
import { IconCreditCard } from '@tabler/icons-react';
import { BankAccountType, KybApplicationStatus } from '@zorro/clients';
import { Alert } from '@zorro/design-system/v2/Alert';
import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import { Pill, PillColor } from '@zorro/design-system/v2/Pill';
import { TabButtonGroup } from '@zorro/design-system/v2/TabButtonGroup';
import { TextInput } from '@zorro/design-system/v2/TextInput';
import {
  FormErrorMessage,
  Group,
  Icon,
  InputWrapper,
  SimpleGrid,
  Stack,
  Text,
} from '@zorro/web/design-system';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { Controller, UseFormReturn } from 'react-hook-form';
import { $enum } from 'ts-enum-util';

import { getBankAccountTypeLabel } from './AddPaymentAccountDetailsStepUtils';
import { PaymentAccountDetailsFormFields } from './PaymentAccountDetailsSchema';

const kybStatusConfig: Record<
  KybApplicationStatus,
  { label: string; color: PillColor }
> = {
  [KybApplicationStatus.APPROVED]: { label: 'Approved', color: 'green' },
  [KybApplicationStatus.PENDING]: { label: 'In Progress', color: 'yellow' },
  [KybApplicationStatus.DENIED]: { label: 'Denied', color: 'red' },
};

type Props = {
  form: UseFormReturn<PaymentAccountDetailsFormFields>;
  isReadOnly?: boolean;
  employerId: string;
};

export const PaymentAccountDetailsForm = ({
  form,
  isReadOnly = false,
  employerId,
}: Props) => {
  const { control, formState } = form;
  const { errors } = formState;

  const { data: kybData, isLoading: isKybLoading } = useMonolithQuery({
    method: 'paymentsControllerGetEmployerKybStatus',
    params: [employerId],
  });

  return (
    <>
      <Alert variant="intro">
        Complete the financial information and integration setup to finalize the
        employer onboarding.
      </Alert>
      <Stack>
        {/* Financial Information */}
        <Card withBorder py="md" px="md">
          <Card.Section withBorder py="md" px="lg">
            <Group gap="xs">
              <Icon size="1.5rem" icon={IconCreditCard} />
              <Text fw={600}>Financial information</Text>
            </Group>
          </Card.Section>
          <Card.Section withBorder py="md" px="lg">
            <Stack>
              <Controller
                name="bankAccountName"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Bank account name"
                    required
                    placeholder="Enter bank account name"
                    fw={500}
                    disabled={isReadOnly}
                  />
                )}
              />
              <FormErrorMessage fieldName="bankAccountName" errors={errors} />

              <SimpleGrid cols={2}>
                <div>
                  <Controller
                    name="bankAccountNumber"
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        {...field}
                        label="Bank account number"
                        required
                        placeholder="Enter account number"
                        fw={500}
                        disabled={isReadOnly}
                      />
                    )}
                  />
                  <FormErrorMessage
                    fieldName="bankAccountNumber"
                    errors={errors}
                  />
                </div>

                <div>
                  <Controller
                    name="routingNumber"
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        {...field}
                        label="Routing number"
                        required
                        placeholder="Enter routing number"
                        fw={500}
                        disabled={isReadOnly}
                      />
                    )}
                  />
                  <FormErrorMessage fieldName="routingNumber" errors={errors} />
                </div>
              </SimpleGrid>
              <SimpleGrid cols={2}>
                <div>
                  <InputWrapper label="Account type">
                    <Controller
                      name="bankAccountType"
                      control={control}
                      render={({ field }) => (
                        <TabButtonGroup
                          {...field}
                          h="2.5rem"
                          isFullWidth
                          size="md"
                        >
                          <TabButtonGroup.List>
                            {$enum(BankAccountType)
                              .getValues()
                              .map((accountType) => (
                                <TabButtonGroup.Tab
                                  key={accountType}
                                  value={accountType}
                                  disabled={isReadOnly}
                                >
                                  {getBankAccountTypeLabel(accountType)}
                                </TabButtonGroup.Tab>
                              ))}
                          </TabButtonGroup.List>
                        </TabButtonGroup>
                      )}
                    />
                  </InputWrapper>
                  <FormErrorMessage
                    fieldName="bankAccountType"
                    errors={errors}
                  />
                </div>
              </SimpleGrid>
            </Stack>
          </Card.Section>
        </Card>

        {/* Card 3: KYB Confirmation */}
        <Card withBorder py="md" px="md">
          <Card.Section withBorder py="md" px="lg">
            <Group justify="space-between">
              <Text fw={600}>KYB Confirmation</Text>
              {isKybLoading ? (
                <Skeleton height={28} width={90} radius="xl" />
              ) : kybData?.status ? (
                <Pill
                  variant="tag"
                  color={kybStatusConfig[kybData.status].color}
                >
                  {kybStatusConfig[kybData.status].label}
                </Pill>
              ) : null}
            </Group>
          </Card.Section>
          <Card.Section withBorder py="md" px="lg">
            <Stack gap="md">
              <Controller
                name="kybConfirmed"
                control={control}
                render={({ field: { value, onChange, ...rest } }) => (
                  <Checkbox
                    {...rest}
                    checked={value}
                    onChange={(event) => onChange(event.currentTarget.checked)}
                    disabled={isReadOnly}
                    isTransparent
                    fw={500}
                    label="I confirm that the Know Your Business (KYB) verification has been completed and all required documentation has been collected and verified."
                  />
                )}
              />
              <FormErrorMessage fieldName="kybConfirmed" errors={errors} />
            </Stack>
          </Card.Section>
        </Card>
      </Stack>
    </>
  );
};
